import { Receipt } from '@mui/icons-material';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import Link from '@mui/material/Link';
import { GridActionsCellItem } from '@mui/x-data-grid';
import {
	GridColDef,
	GridRenderCellParams,
	GridRowParams,
} from '@mui/x-data-grid/models';
import React, { useState } from 'react';
import { Link as LinkRouter, useNavigate } from 'react-router-dom';
import {
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
	useAuthContext,
} from '../../context/auth';
import { useConfigContext } from '../../context/config';
import { CurrencyCode } from '../../types/currencyCode';
import {
	GraceLapseReinstatementResponse,
	Policy2,
	PolicyCharacteristics,
} from '../../types/policy';
import {
	formatCurrency,
	formatName,
	getActiveCharacteristics,
	getEntityNameFrom_DescriptionField,
	getPolicyStatus,
	sortCharacteristicsByStartDate,
} from '../../util/util';
import BalanceTransferModal from '../cashBalance/components/BalanceTransferModal';
import CashBalanceDisplayPill from '../cashBalance/components/CashBalanceDisplayPill';
import { CASH_BALANCE__TRANSFER } from '../cashBalance/data/constants';
import { CREATE_CATCH_UP__SHORTCUT } from '../catchUps/data/constants';
import { CREATE_DISBURSEMENT__SHORTCUT } from '../disbursements/data/constants';
import { dateConversion } from '../fastForm/utils';
import GridWithToolbar from '../grid/GridWithToolbar';
import { getCurrentGraceLapse } from '../search/util';
import {
	POLICYHOLDER__POLICIES__TABLE_HEADER,
	POLICYHOLDER__POLICIES__TABLE_HEADER__CASH_BALANCE,
	POLICYHOLDER__POLICIES__TABLE_HEADER__CASH_BALANCE_CLASSNAME,
	POLICYHOLDER__POLICIES__TABLE_HEADER__CASH_BALANCE_FIELD_NAME,
	POLICYHOLDER__POLICIES__TABLE_HEADER__POLICY_ID,
	POLICYHOLDER__POLICIES__TABLE_HEADER__POLICY_ID_CLASSNAME,
	POLICYHOLDER__POLICIES__TABLE_HEADER__POLICY_ID_FIELD_NAME,
	POLICYHOLDER__POLICIES__TABLE_HEADER__POLICY_NAME,
	POLICYHOLDER__POLICIES__TABLE_HEADER__POLICY_NAME_CLASSNAME,
	POLICYHOLDER__POLICIES__TABLE_HEADER__POLICY_NAME_FIELD_NAME,
	POLICYHOLDER__POLICIES__TABLE_HEADER__PRODUCT,
	POLICYHOLDER__POLICIES__TABLE_HEADER__PRODUCT_CLASSNAME,
	POLICYHOLDER__POLICIES__TABLE_HEADER__PRODUCT_FIELD_NAME,
	POLICYHOLDER__POLICIES__TABLE_HEADER__STATUS,
	POLICYHOLDER__POLICIES__TABLE_HEADER__STATUS_CLASSNAME,
	POLICYHOLDER__POLICIES__TABLE_HEADER__STATUS_FIELD_NAME,
	POLICYHOLDER__POLICIES__TABLE_HEADER__TERM,
	POLICYHOLDER__POLICIES__TABLE_HEADER__TERM_CLASSNAME,
	POLICYHOLDER__POLICIES__TABLE_HEADER__TERM_FIELD_NAME,
	POLICYHOLDER__POLICIES__TABLE_ROW_ID,
} from './data/constants';

interface ModalInfo {
	cashBalanceAmount: string;
	cashBalanceCurrency: CurrencyCode | null;
	cashBalanceLocator: string;
	open: boolean;
	policyFrom?: string;
}

interface PoliciesTableProps {
	policies: Policy2[];
	graceLapses: GraceLapseReinstatementResponse[];
	loading: boolean;
	isRefetching: boolean;
}

interface PoliciesTableRow {
	status: string;
	product: string;
	cashBalanceAmountFormatted?: string;
	cashBalanceAmount: string;
	cashBalanceCurrency: string;
	cashBalanceLocator: string;
	displayId: string;
	policyName: string;
	policyIsIssued: boolean;
	startDate: number;
	endDate: number;
	startDateFormatted: string;
	endDateFormatted: string;
}

const NewPoliciesTable = (props: PoliciesTableProps) => {
	const { policies, graceLapses, loading, isRefetching } = props;
	const authData = useAuthContext();
	const config = useConfigContext();
	const transferConfigs =
		config?.cashManagementConfig?.configuration?.cashTransfers;
	const navigate = useNavigate();
	const timezone = authData.auth?.timezone;
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];
	const { hasPermission } = authData;

	const [transferBalance, setTransferBalance] = useState<ModalInfo>({
		cashBalanceAmount: null,
		cashBalanceCurrency: null,
		cashBalanceLocator: null,
		open: false,
		policyFrom: null,
	});

	const LinkCell = (params: GridRenderCellParams<PoliciesTableRow>) => (
		<Link
			underline='hover'
			component={LinkRouter}
			to={`/policy/${params.row.displayId}/details`}
		>
			{params.value}
		</Link>
	);

	const TermCell = (params: GridRenderCellParams<PoliciesTableRow>) => (
		<>
			{params.row.startDateFormatted}
			{' - '}
			{params.row.endDateFormatted}
		</>
	);

	const CashBalanceCell = (params: GridRenderCellParams<PoliciesTableRow>) => {
		return (
			<CashBalanceDisplayPill
				amount={params.row.cashBalanceAmount + ''}
				currencyCode={params.row.cashBalanceCurrency}
			/>
		);
	};

	const getRowActions = (params: GridRowParams<PoliciesTableRow>) => {
		const rowActions: JSX.Element[] = [];
		// only show actions for issued policies
		if (!params.row.policyIsIssued) {
			return rowActions;
		}
		if (
			parseFloat(params.row.cashBalanceAmount) < 0 &&
			hasPermission('catchUpInvoice.issue') &&
			cashManagementEnabled
		) {
			rowActions.push(
				<GridActionsCellItem
					icon={<PlaylistAddIcon />}
					onClick={() =>
						navigate(`/policy/${params.row.displayId}/catchUps/new`)
					}
					label={CREATE_CATCH_UP__SHORTCUT}
					showInMenu
				/>,
			);
		}
		if (
			parseFloat(params.row.cashBalanceAmount) > 0 &&
			cashManagementEnabled &&
			hasPermission('disbursement.create')
		) {
			rowActions.push(
				<GridActionsCellItem
					icon={<Receipt />}
					onClick={() =>
						navigate(`/policy/${params.row.displayId}/disbursements/new`)
					}
					label={CREATE_DISBURSEMENT__SHORTCUT}
					showInMenu
				/>,
			);
		}
		if (
			cashManagementEnabled &&
			hasPermission('cashBalance.transfer') &&
			transferConfigs?.length
		) {
			rowActions.push(
				<GridActionsCellItem
					icon={<CurrencyExchangeIcon />}
					onClick={() =>
						setTransferBalance({
							cashBalanceAmount: params.row.cashBalanceAmount,
							cashBalanceCurrency: params.row
								.cashBalanceCurrency as CurrencyCode,
							cashBalanceLocator: params.row.cashBalanceLocator,
							open: true,
							policyFrom: params.row.displayId,
						})
					}
					label={CASH_BALANCE__TRANSFER}
					showInMenu
				/>,
			);
		}
		return rowActions;
	};

	const columns: GridColDef<PoliciesTableRow>[] = [
		{
			field: POLICYHOLDER__POLICIES__TABLE_HEADER__POLICY_ID_FIELD_NAME,
			headerName: POLICYHOLDER__POLICIES__TABLE_HEADER__POLICY_ID,
			headerClassName:
				POLICYHOLDER__POLICIES__TABLE_HEADER__POLICY_ID_CLASSNAME,
			flex: 0.2,
			renderCell: LinkCell,
		},
		{
			field: POLICYHOLDER__POLICIES__TABLE_HEADER__POLICY_NAME_FIELD_NAME,
			headerName: POLICYHOLDER__POLICIES__TABLE_HEADER__POLICY_NAME,
			headerClassName:
				POLICYHOLDER__POLICIES__TABLE_HEADER__POLICY_NAME_CLASSNAME,
			flex: 0.2,
		},
		{
			field: POLICYHOLDER__POLICIES__TABLE_HEADER__PRODUCT_FIELD_NAME,
			headerName: POLICYHOLDER__POLICIES__TABLE_HEADER__PRODUCT,
			headerClassName: POLICYHOLDER__POLICIES__TABLE_HEADER__PRODUCT_CLASSNAME,
			flex: 0.2,
		},
		{
			field: POLICYHOLDER__POLICIES__TABLE_HEADER__TERM_FIELD_NAME,
			headerName: POLICYHOLDER__POLICIES__TABLE_HEADER__TERM,
			headerClassName: POLICYHOLDER__POLICIES__TABLE_HEADER__TERM_CLASSNAME,
			flex: 0.3,
			renderCell: TermCell,
		},
		...(cashManagementEnabled && hasPermission('cashBalance.fetch')
			? [
					{
						field:
							POLICYHOLDER__POLICIES__TABLE_HEADER__CASH_BALANCE_FIELD_NAME,
						headerName: POLICYHOLDER__POLICIES__TABLE_HEADER__CASH_BALANCE,
						headerClassName:
							POLICYHOLDER__POLICIES__TABLE_HEADER__CASH_BALANCE_CLASSNAME,
						flex: 0.2,
						renderCell: CashBalanceCell,
					},
			  ]
			: []),
		{
			field: POLICYHOLDER__POLICIES__TABLE_HEADER__STATUS_FIELD_NAME,
			headerName: POLICYHOLDER__POLICIES__TABLE_HEADER__STATUS,
			headerClassName: POLICYHOLDER__POLICIES__TABLE_HEADER__STATUS_CLASSNAME,
			flex: 0.2,
		},
		{
			field: 'actions',
			type: 'actions',
			flex: 0.2,
			sortable: false,
			getActions: getRowActions,
		},
	];

	const formattedData: PoliciesTableRow[] = policies.map((policy) => {
		const policyGraceLapses = graceLapses.filter(
			(g) => g?.gracePeriod?.policyLocator === policy.locator,
		);

		const startDateObject = dateConversion.convertTimestampToDayJS(
			policy.originalContractStartTimestamp,
			timezone,
		);

		const endDateObject = dateConversion.convertTimestampToDayJS(
			policy.effectiveContractEndTimestamp,
			timezone,
		);

		const latestActivePolicyCharacteristic = sortCharacteristicsByStartDate(
			getActiveCharacteristics(
				policy.characteristics,
			) as PolicyCharacteristics[],
		).pop();

		const policyName = getEntityNameFrom_DescriptionField(
			latestActivePolicyCharacteristic,
		);

		const currentGraceLapse = getCurrentGraceLapse(policyGraceLapses);

		const cashBalanceParsed = parseFloat(policy.cashBalance?.amount);

		const row: PoliciesTableRow = {
			status: getPolicyStatus(policy, currentGraceLapse),
			product: formatName(policy.productName),
			cashBalanceAmountFormatted: formatCurrency(
				cashBalanceParsed,
				policy.cashBalance?.currency as CurrencyCode,
			),
			cashBalanceAmount: policy.cashBalance?.amount,
			cashBalanceCurrency: policy.cashBalance?.currency,
			cashBalanceLocator: policy.cashBalance?.locator,
			displayId: policy.displayId,
			policyName: policyName || '-',
			policyIsIssued: !!policy.issuedTimestamp,
			startDate: parseInt(policy.originalContractStartTimestamp),
			endDate: parseInt(policy.originalContractEndTimestamp),
			startDateFormatted: startDateObject.format('YYYY/MM/DD'),
			endDateFormatted: endDateObject.format('YYYY/MM/DD'),
		};

		return row;
	});

	return (
		<>
			<GridWithToolbar
				rowId={POLICYHOLDER__POLICIES__TABLE_ROW_ID}
				title={POLICYHOLDER__POLICIES__TABLE_HEADER}
				isRefetching={isRefetching}
				data={formattedData || []}
				columns={columns}
				loading={loading}
				autoHeight
				pageSize={5}
			/>
			{transferBalance.open && (
				<BalanceTransferModal
					open={transferBalance.open}
					cashBalanceAmount={transferBalance.cashBalanceAmount}
					cashBalanceCurrency={transferBalance.cashBalanceCurrency}
					cashBalanceLocator={transferBalance.cashBalanceLocator}
					policyFrom={transferBalance.policyFrom}
					handleClose={() =>
						setTransferBalance({
							cashBalanceAmount: null,
							cashBalanceCurrency: null,
							cashBalanceLocator: null,
							open: false,
							policyFrom: null,
						})
					}
				/>
			)}
		</>
	);
};

export default NewPoliciesTable;
